// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { CircularProgress, Divider, Tooltip, Typography } from '@mui/material';
import type { Document } from '@shared/data-access/types/engine-types';
import { AppIcon, ShowInAppClient } from '@shared/ui-kit';
import {
  IconButton,
  type IconButtonProps,
} from '@shared/ui-kit/components/icon-button';
import { Input } from '@shared/ui-kit/components/input';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import type { TFunction } from 'i18next';
import type { ChangeEvent, FC, ReactNode } from 'react';
import { DocumentFileListItem } from '../document-file-list-item/document-file-list-item';

export interface EntityDocumentsLinkPanelProps {
  readonly t: TFunction;
  readonly showLinkPanel: boolean;
  readonly hasRelatableDocuments: boolean;
  readonly showRelatableSearch: boolean;
  readonly relatableSearchQuery: string;
  readonly onRelatableSearchQueryChange: (value: string) => void;
  readonly relatableDocuments: readonly Document[];
  readonly linkingDocumentId: number | null;
  readonly onLinkDocument: (documentId: number) => void;
  readonly showDivider?: boolean;
}

const SectionHeading = ({ children }: { readonly children: ReactNode }) => (
  <Typography
    component="h3"
    variant="subtitle2"
    className="!mb-0 !block !w-full !text-base !font-semibold !uppercase !text-primary-medium"
  >
    {children}
  </Typography>
);

const EmptyState = ({ children }: { readonly children: ReactNode }) => (
  <div
    className="rounded-lg border border-section bg-page px-4 py-5 text-center"
    data-testid="entity-documents-link-empty"
  >
    <Typography
      component="p"
      variant="body2"
      className="!block !text-primary-medium"
    >
      {children}
    </Typography>
  </div>
);

const ActionIconButton = ({
  tooltip,
  ...props
}: IconButtonProps & { readonly tooltip: string }) => (
  <Tooltip title={tooltip} placement="top">
    <span className="inline-flex shrink-0 items-center justify-center">
      <IconButton {...props} />
    </span>
  </Tooltip>
);

export const EntityDocumentsLinkPanel: FC<EntityDocumentsLinkPanelProps> = ({
  t,
  showLinkPanel,
  hasRelatableDocuments,
  showRelatableSearch,
  relatableSearchQuery,
  onRelatableSearchQueryChange,
  relatableDocuments,
  linkingDocumentId,
  onLinkDocument,
  showDivider = true,
}) => {
  if (!showLinkPanel || !hasRelatableDocuments) {
    return null;
  }

  return (
    <ShowInAppClient>
      <>
        {showDivider ? <Divider className="!border-section" /> : null}

        <section
          className="flex w-full flex-col gap-3"
          data-testid="entity-documents-link-panel"
        >
          <SectionHeading>
            <Translation
              i18nKey="documents.entity-section.link-title"
              ns="components"
            />
          </SectionHeading>

          {showRelatableSearch ? (
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                <AppIcon icon="Search" color="disabled" size="base" />
              </span>
              <Input
                type="search"
                autoComplete="off"
                className="w-full pl-10"
                placeholder={t('documents.entity-section.search-placeholder', {
                  ns: 'components',
                })}
                value={relatableSearchQuery}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onRelatableSearchQueryChange(event.target.value)
                }
                data-testid="entity-documents-search"
              />
            </div>
          ) : null}

          {relatableDocuments.length === 0 ? (
            <EmptyState>
              <Translation
                i18nKey="documents.entity-section.search-empty"
                ns="components"
              />
            </EmptyState>
          ) : (
            <ul className="flex flex-col gap-1">
              {relatableDocuments.map(document => {
                const fileCount = document.files?.length ?? 0;
                const isLinking = linkingDocumentId === document.id;
                const linkLabel = t('documents.entity-section.link-aria', {
                  ns: 'components',
                  title: document.title,
                });

                return (
                  <DocumentFileListItem
                    key={document.id}
                    fileName={document.title}
                    fileSize={0}
                    title={document.title}
                    metaLabel={t('documents.file-count', {
                      ns: 'components',
                      count: fileCount,
                    })}
                    displayIcon="FileText"
                    surface="section"
                    testId={`entity-relatable-document-${document.id}`}
                    action={
                      isLinking ? (
                        <CircularProgress size={20} />
                      ) : (
                        <ActionIconButton
                          icon="Plus"
                          variant="icon"
                          size="xl"
                          color="main"
                          tooltip={linkLabel}
                          aria-label={linkLabel}
                          data-testid={`entity-document-link-${document.id}`}
                          onClick={() => void onLinkDocument(document.id)}
                        />
                      )
                    }
                  />
                );
              })}
            </ul>
          )}
        </section>
      </>
    </ShowInAppClient>
  );
};

EntityDocumentsLinkPanel.displayName = 'EntityDocumentsLinkPanel';
